import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import config from "../../config";

import { createYearlyPlan } from "../../actions/postAction";
import { FIVE_YEAR_CATEGORIES } from "./fiveYearCategories";
import "./CreateYearPlan.css";
import PreviewPlan from "./PreviewPlan";
import PlanQuestionnaire from "./PlanQuestionnaire";

const DRAFTS_KEY = "dmyor_vision_drafts";
const TEXT_LIMIT = 500;

/* ── Draft utilities ── */
const loadDrafts = () => {
  try { return JSON.parse(localStorage.getItem(DRAFTS_KEY)) || []; }
  catch { return []; }
};
const saveDraftToStorage = (draft) => {
  const drafts = loadDrafts();
  const idx = drafts.findIndex((d) => d.id === draft.id);
  if (idx >= 0) drafts[idx] = draft;
  else drafts.unshift(draft);
  localStorage.setItem(DRAFTS_KEY, JSON.stringify(drafts));
};

const EXTRA_CATEGORIES = [
  "Purpose",
  "Spirituality",
  "Identity",
  "Social",
  "Creative",
  "Art",
  "Writing",
  "Travel",
  "Fitness",
  "Wealth",
  "Legacy",
  "Social Media",
];


/* ── CUSTOM CATEGORY MODAL ── */
const CustomCategoryModal = ({ existing, onAdd, onClose }) => {
  const [selected, setSelected] = useState([]);
  const [customInput, setCustomInput] = useState("");

  const toggle = (key) => {
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleAddCustom = () => {
    const trimmed = customInput.trim();
    if (!trimmed) return;
    if (existing.includes(trimmed) || selected.includes(trimmed)) {
      setCustomInput("");
      return;
    }
    setSelected((prev) => [...prev, trimmed]);
    setCustomInput("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleAddCustom();
  };

  const available = EXTRA_CATEGORIES.filter((c) => !existing.includes(c));

  return (
    <div className="ccm-overlay" onClick={onClose}>
      <div className="ccm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ccm-header">
          <span className="ccm-title">Add Categories</span>
          <button className="ccm-close" onClick={onClose}>✕</button>
        </div>

        <p className="ccm-hint">Pick from the list or type your own.</p>

        {selected.filter((s) => !EXTRA_CATEGORIES.includes(s)).length > 0 && (
          <div className="ccm-custom-tags">
            {selected
              .filter((s) => !EXTRA_CATEGORIES.includes(s))
              .map((tag) => (
                <span key={tag} className="ccm-custom-tag">
                  {tag}
                  <button className="ccm-tag-remove" onClick={() => toggle(tag)} type="button">✕</button>
                </span>
              ))}
          </div>
        )}

        <div className="ccm-grid">
          {available.length === 0 ? (
            <p className="ccm-empty">All preset categories already added.</p>
          ) : (
            available.map((key) => (
              <button
                key={key}
                className={`ccm-item ${selected.includes(key) ? "selected" : ""}`}
                onClick={() => toggle(key)}
              >
                <span className="ccm-label">{key}</span>
                {selected.includes(key) && <span className="ccm-check">✓</span>}
              </button>
            ))
          )}
        </div>

        <div className="ccm-custom-input-row">
          <input
            type="text"
            className="ccm-custom-input"
            placeholder="e.g. Gaming, Side hustle..."
            value={customInput}
            maxLength={30}
            onChange={(e) => setCustomInput(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button
            className="ccm-custom-add-btn"
            onClick={handleAddCustom}
            disabled={!customInput.trim()}
            type="button"
          >
            Add
          </button>
        </div>

        <div className="ccm-footer">
          <button className="ccm-cancel" onClick={onClose}>Cancel</button>
          <button
            className="ccm-confirm"
            disabled={selected.length === 0}
            onClick={() => { onAdd(selected); onClose(); }}
          >
            Add {selected.length > 0 ? `(${selected.length})` : ""}
          </button>
        </div>
      </div>
    </div>
  );
};


/* ── MAIN COMPONENT ── */
const CreateYearPlan = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.user);

  // Pre-load from draft if navigated here via Drafts page
  const incomingDraft = location.state?.draft || null;

  const [draftId, setDraftId] = useState(incomingDraft?.id || null);
  const [durationInYears, setDurationInYears] = useState(incomingDraft?.durationInYears || 5);
  const [planData, setPlanData] = useState(incomingDraft?.planData || {});
  const [openSections, setOpenSections] = useState(incomingDraft?.openSections || {});
  const [step, setStep] = useState(incomingDraft ? 1 : 0);
  const [btnloading, setbtnloading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [planName, setPlanName] = useState(incomingDraft?.planName || incomingDraft?.name || "");
  const [customCategories, setCustomCategories] = useState(incomingDraft?.customCategories || []);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const [nameConfirmed, setNameConfirmed] = useState(!!(incomingDraft?.planName || incomingDraft?.name));

  const handleSaveDraft = () => {
    const filledCount = Object.values(planData).filter((v) => v && v.trim()).length;
    const categories = Object.keys(openSections).filter((k) => openSections[k]);
    const draft = {
      id: draftId || `draft_${Date.now()}`,
      name: planName.trim() || "Untitled Draft",
      planName: planName.trim() || "Untitled Draft",
      savedAt: new Date().toISOString(),
      durationInYears,
      planData,
      openSections,
      customCategories,
      filledCount,
    };
    if (!draftId) setDraftId(draft.id);
    saveDraftToStorage(draft);
    setDraftSaved(true);
    toast.success("Draft saved");
    setTimeout(() => setDraftSaved(false), 2000);

    // Fire-and-forget analytics ping
    axios.post(
      `${config.apiUrl}/api/v1/draft/ping`,
      { draftName: draft.name, durationInYears, categories, filledCount },
      { withCredentials: true }
    ).catch(() => {}); // silent — never block the user
  };

  const toggleSection = (key) => {
    const hasContent = planData[key] && planData[key].trim().length > 0;
    setOpenSections((prev) => {
      if (prev[key] && hasContent) return prev;
      return { ...prev, [key]: !prev[key] };
    });
  };

  const handleChange = (key, value) => {
    setPlanData((prev) => ({ ...prev, [key]: value }));
  };


  const handleAddCategories = (keys) => {
    setCustomCategories((prev) => [...prev, ...keys]);
    const openMap = {};
    keys.forEach((k) => { openMap[k] = true; });
    setOpenSections((prev) => ({ ...prev, ...openMap }));
  };

  const handleSavePlan = async () => {
    try {
      setbtnloading(true);
      const payload = { durationInYears, sections: planData, name: planName };
      const result = await dispatch(createYearlyPlan(payload));
      const planId = result?.planId || result;
      // Remove draft from localStorage
      if (draftId) {
        const drafts = loadDrafts().filter((d) => d.id !== draftId);
        localStorage.setItem(DRAFTS_KEY, JSON.stringify(drafts));
      }
      // Remove from admin panel (fire-and-forget)
      axios.delete(`${config.apiUrl}/api/v1/draft/ping`, { withCredentials: true }).catch(() => {});
      toast.success(`${durationInYears} Year Plan created`);
      navigate("/dashboard/plan-transition", { state: { planId, durationInYears } });
    } finally {
      setbtnloading(false);
    }
  };

  const allCategoryKeys = [
    ...Object.keys(FIVE_YEAR_CATEGORIES).filter((k) => k !== "Other"),
    ...customCategories,
  ];

  return (
    <>
      {step === 0 && (
        <PlanQuestionnaire
          onComplete={({ duration, categories }) => {
            setDurationInYears(duration);
            const openMap = {};
            categories.forEach((cat) => { openMap[cat] = true; });
            setOpenSections(openMap);
            setStep(1);
          }}
        />
      )}

      {step === 1 && (
        <div className="five-year-container">

          {/* HEADER */}
          <h2>Create your Reality.</h2>
          <p className="subtitle">
            Write the reality that doesn't exist yet. Choose your timeline,
            select the categories you're building, and write.
          </p>
          <Link
            to="/blog/how-to-create-your-first-vision"
            target="_blank"
            rel="noopener noreferrer"
            className="create-howto-link"
          >
            Not sure how to write? See examples and tips →
          </Link>

          {/* TIMELINE */}
          <div style={{ marginTop: "28px" }}>
            <p className="hint-text" style={{ marginBottom: "10px" }}>
              How far ahead are we talking?
            </p>

            {/* Quick-pick buttons */}
            {/* <div className="category-buttons" style={{ marginBottom: "16px" }}>
              {[1, 3, 5].map((year) => (
                <button
                  key={year}
                  type="button"
                  className={`category-btn ${durationInYears === year ? "active" : ""}`}
                  onClick={() => setDurationInYears(year)}
                >
                  {year} {year === 1 ? "Year" : "Years"}
                </button>
              ))}
            </div> */}

            {/* Custom slider */}
            <div className="cyp-slider-wrap">
              <div className="cyp-slider-header">
                <span className={`cyp-slider-value ${durationInYears === 5 ? "cyp-slider-value-p5" : ""}`}>
                  {durationInYears === 5
                    ? "PROJECT-5"
                    : `${durationInYears} ${durationInYears === 1 ? "year" : "years"}`}
                </span>
                {durationInYears === 5 && (
                  <Link to="/project-5" target="_blank" rel="noopener noreferrer" className="cyp-p5-info-link">
                    <span className="cyp-p5-info-icon">?</span> What is this?
                  </Link>
                )}
              </div>
              <input
                type="range"
                className="cyp-slider"
                min={1}
                max={5}
                step={1}
                value={durationInYears}
                onChange={(e) => setDurationInYears(Number(e.target.value))}
                aria-label="Vision duration in years"
                style={{ "--val": durationInYears }}
              />
              <div className="cyp-slider-ticks">
                {Array.from({ length: 5 }, (_, i) => i + 1).map((y) =>
                  y === 5 ? (
                    <span
                      key={y}
                      className={`cyp-tick cyp-tick-p5 ${y === durationInYears ? "active" : ""}`}
                    >
                      P5
                    </span>
                  ) : (
                    <span
                      key={y}
                      className={`cyp-tick ${y === durationInYears ? "active" : ""}`}
                    >
                      {y}
                    </span>
                  )
                )}
              </div>
            </div>

            <div className="cyp-name-block">
              <p className="hint-text" style={{ marginBottom: "8px" }}>
                Give your vision a name <span className="cyp-required">*</span>
              </p>
              <div className="cyp-name-row">
                <input
                  type="text"
                  className="cyp-name-input"
                  placeholder='e.g. "journey to my ideal reality"'
                  value={planName}
                  maxLength={60}
                  onChange={(e) => {
                    setPlanName(e.target.value);
                    if (nameConfirmed && !e.target.value.trim()) setNameConfirmed(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && planName.trim()) setNameConfirmed(true);
                  }}
                />
                {!nameConfirmed && (
                  <button
                    type="button"
                    className="cyp-name-continue-btn"
                    disabled={!planName.trim()}
                    onClick={() => setNameConfirmed(true)}
                  >
                    Continue
                  </button>
                )}
              </div>
            </div>
          </div>

          {nameConfirmed && (
            <>
              {/* DIVIDER */}
              <div className="cyp-divider" />

              {/* DIMENSIONS */}
              <p className="hint-text" style={{ marginBottom: "10px" }}>
                Which categories are you building?
              </p>
              <div className="cyp-categories">
                {allCategoryKeys.map((key) => (
                  <button
                    key={key}
                    type="button"
                    className={`cyp-cat-btn ${openSections[key] ? "active" : ""}`}
                    onClick={() => toggleSection(key)}
                  >
                    {key}
                  </button>
                ))}
                <button
                  type="button"
                  className="cyp-cat-btn cyp-add-btn"
                  onClick={() => setShowCategoryModal(true)}
                >
                  More
                </button>
              </div>

              {/* TIP */}
              {Object.values(openSections).some(Boolean) && (
                <p className="cyp-writing-tip">
                  Writing what your life will look like isn't easy. Take your time, sit with it, and write what's actually true to you.
                </p>
              )}

              {/* EDITORS */}
              <div className="editor-sections">
                {Object.keys(openSections).map(
                  (key) =>
                    openSections[key] && (
                      <div key={key} className="editor-card">
                        <div className="editor-header">
                          <span className="editor-pill">{key}</span>
                          <button
                            type="button"
                            className="collapse-btn"
                            onClick={() => toggleSection(key)}
                          >
                            Collapse
                          </button>
                        </div>
                        <textarea
                          className="editor-textarea"
                          placeholder="What's on your mind?"
                          value={planData[key] || ""}
                          maxLength={TEXT_LIMIT}
                          onChange={(e) => handleChange(key, e.target.value)}
                        />
                        <div className="editor-footer">
                          <span className="char-counter">
                            {(planData[key]?.length || 0)}/{TEXT_LIMIT}
                          </span>
                        </div>
                      </div>
                    )
                )}
              </div>

              {/* FOOTER */}
              <div className="plan-footer">
                <button
                  className="secondary-btn"
                  type="button"
                  onClick={() => setShowPreview(true)}
                  disabled={Object.keys(planData).length === 0}
                >
                  Preview
                </button>
                <button
                  className="cyp-draft-btn"
                  type="button"
                  onClick={handleSaveDraft}
                  disabled={Object.keys(planData).length === 0 && !planName.trim()}
                >
                  {draftSaved ? "Saved ✓" : "Save Draft"}
                </button>
                <button
                  className="primary-btn"
                  type="button"
                  onClick={handleSavePlan}
                  disabled={Object.keys(planData).length === 0 || !planName.trim() || btnloading}
                >
                  {btnloading ? "Creating..." : "Create"}
                </button>
              </div>
              <div className="cyp-drafts-link-row">
                <Link to="/dashboard/drafts" className="cyp-drafts-link">
                  View saved drafts →
                </Link>
              </div>
            </>
          )}

        </div>
      )}


      {showPreview && (
        <div className="preview-overlay">
          <div className="preview-modal">
            <div className="preview-top-bar">
              <span>Record Preview</span>
              <button onClick={() => setShowPreview(false)}>Close</button>
            </div>
            <PreviewPlan
              durationInYears={durationInYears}
              planData={planData}
              user={user}
            />
          </div>
        </div>
      )}

      {showCategoryModal && (
        <CustomCategoryModal
          existing={allCategoryKeys}
          onAdd={handleAddCategories}
          onClose={() => setShowCategoryModal(false)}
        />
      )}
    </>
  );
};

export default CreateYearPlan;